import { graphql } from '~/graphql/generated/gql';

export const occupationBasicDataFragment = graphql(`
  fragment OccupationBasicData on Occupation {
    id
    title
    slug
    wage
    imageUrl
    description
    skills {
      name
      importance
    }
    isFastGrowing
    localWage {
      stateName
      wage
    }
  }
`);

export const institutionBasicDataFragment = graphql(`
  fragment InstitutionBasicData on Institution {
    id
    name
    imageUrl
    sector
    sites {
      state
      suburb
      postcode
    }
    costs {
      inDistrictAvgTuitionFee
    }
    details {
      avgGpa
    }
    description
  }
`);

export const searchOccupationsQuery = graphql(`
  query searchOccupations($search: String!, $limit: Int) {
    search {
      occupations(search: $search, limit: $limit) {
        id
        title
      }
    }
  }
`);

export const searchInstitutionsQuery = graphql(`
  query searchInstitutions($search: String!, $limit: Int) {
    search {
      institutions(search: $search, limit: $limit) {
        id
        name
        imageUrl
        sector
        sites {
          state
          suburb
        }
      }
    }
  }
`);

export const searchCourseInstitutionsQuery = graphql(`
  query searchCourseInstitutions($search: String!, $limit: Int, $routeKey: String, $occupationId: String) {
    search {
      courseInstitutions(search: $search, limit: $limit, routeKey: $routeKey, occupationId: $occupationId) {
        id
        name
        levelName
        durationYears {
          median
          min
          max
        }
        entry {
          noEntryScoreRequired
          score {
            type
            min
            median
            max
          }
        }
        feeAmount
        feeScheme
        institutionSector
        institutionLogoUrl
        institutionLocation
        institutionSiteCount
        institution {
          id
          name
        }
      }
    }
  }
`);

export const SEARCH_FETCH_OCCUPATIONS = graphql(`
  query searchFetchOccupations($occupationIds: [String!]!) {
    search {
      fetchOccupations(occupationIds: $occupationIds) {
        ...OccupationBasicData
        jobResponsibilities
        careerConsiderations {
          title
          description
        }
      }
    }
  }
`);

export const SEARCH_FETCH_OCCUPATION_BY_SLUG = graphql(`
  query searchFetchOccupationBySlug($slug: String!) {
    search {
      fetchOccupationBySlug(slug: $slug) {
        ...OccupationBasicData
        jobResponsibilities
        careerConsiderations {
          title
          description
        }
      }
    }
  }
`);

export const SEARCH_FETCH_ALL_INDUSTRY_HUBS = graphql(`
  query searchFetchAllIndustryHubs {
    search {
      fetchAllIndustryHubs {
        slug
        title
        heroImagePath
      }
    }
  }
`);

export const RECOMMENDED_INDUSTRY_HUBS = graphql(`
  query recommendedIndustryHubs {
    core {
      user {
        self {
          id
          recommendedIndustryHubs {
            slug
            title
            heroImagePath
          }
        }
      }
    }
  }
`);

export const SEARCH_FETCH_INDUSTRY_HUB_BY_SLUG = graphql(`
  query searchFetchIndustryHubBySlug($slug: String!) {
    search {
      fetchIndustryHubBySlug(slug: $slug) {
        considerationAreaId
        slug
        title
        description
        heroImagePath
        atAGlance {
          attribution {
            label
            link
          }
          items {
            key
            title
            subtitle
            value
          }
        }
        why {
          description
          items
        }
        outlook {
          key
          title
          description
        }
        entryRequirements {
          label
          value
        }
        methodology {
          topJobs
          topInstitutions
        }
        topJobs {
          id
          title
          slug
          imageUrl
          wage
        }
        institutions {
          id
          name
          slug
          logoUrl
          location
          entryScore {
            type
            min
            median
            max
          }
        }
        partner {
          id
          name
          iconFile {
            id
            publicUrl
          }
        }
      }
    }
  }
`);

export const SEARCH_FETCH_INSTITUTIONS = graphql(`
  query searchFetchInstitutions($institutionIds: [String!]!) {
    search {
      fetchInstitutions(institutionIds: $institutionIds) {
        ...InstitutionBasicData
      }
    }
  }
`);

export const SEARCH_FETCH_INSTITUTION_BY_SLUG = graphql(`
  query searchFetchInstitutionBySlug($slug: String!) {
    search {
      fetchInstitutionBySlug(slug: $slug) {
        ...InstitutionBasicData
      }
    }
  }
`);

/** Shared by the public fetch-by-slug query and the authenticated dashboard data request. */
export const institutionProfileDataFragment = graphql(`
  fragment InstitutionProfileData on InstitutionProfile {
    id
    name
    slug
    sector
    isTop50
    websiteUrl
    description
    images {
      heroImagePath
      logoPath
      items {
        imagePath
      }
    }
    location {
      areaMidName
      areaLocalName
      state
      suburbOrCity
      dataset
    }
    costs {
      courseFee {
        median
        min
        max
      }
      internationalFee {
        median
        min
        max
      }
      medianRent {
        amount
      }
      priceOfAttendance {
        academicYear
        tuitionFeesInDistrict
        tuitionFeesInState
        tuitionFeesOutOfState
        booksAndSupplies
        roomAndBoardOnCampus
      }
    }
    study {
      entryScores {
        type
        min
        median
        max
      }
      qualificationMix {
        undergraduatePct
        postgraduatePct
        vocationalPct
      }
      fieldsOfStudy {
        label
        pct
      }
      totalCourses
      facultyCount
    }
    campuses {
      siteId
      name
      fullAddress
      latitude
      longitude
      isPrimary
    }
    topVibeMetrics {
      key
      label
      direction
      signalHigh
      signalLow
      description
    }
    socialPosts {
      postUrl
      platform
      handle
      caption
      mediaPath
      hashtags
      stats {
        playCount
        likeCount
        commentCount
      }
    }
    topOccupations {
      id
      name
      position
    }
    promotions {
      id
      type
      # The field lists live with the promotion documents (documents/institutionPromotions.ts), so
      # the public sections and the editor cannot drift apart.
      config {
        __typename
        ...InstitutionEventPromotionFields
        ...InstitutionAlumniPromotionFields
        ...InstitutionAreaOfStudyPromotionFields
      }
    }
  }
`);

export const SEARCH_FETCH_INSTITUTION_PROFILE_BY_SLUG = graphql(`
  query searchFetchInstitutionProfileBySlug($slug: String!) {
    search {
      fetchInstitutionProfileBySlug(slug: $slug) {
        ...InstitutionProfileData
      }
    }
  }
`);
